// packages/chat-domain/src/messageBody.ts
//
// A tiny, dependency-free Markdown parser for chat message bodies. Chat replies
// (AI bot or a human agent) are authored in light Markdown — **bold**, *italic*,
// [links](url), bare URLs, and the occasional small GFM table. The shared
// ChatMessageList renders the result so links are real/clickable (opening in a
// new tab) and tables become real <table>s instead of raw pipe text.
//
// Why not a Markdown library: the renderer must bundle into the embed chat
// widget (CRA/webpack, browser-only), tables must render INLINE inside a narrow
// chat bubble (no block-level <p>/<h1> from a generic renderer), and we never
// want raw HTML (XSS). This parser returns a small typed tree; the renderer maps
// it to React + MUI elements, so every text node is React-escaped and only
// http(s)/mailto/tel hrefs survive — there is no `dangerouslySetInnerHTML`.
//
// Scope is deliberately small (what the concierge actually emits): inline
// emphasis with `*`/`**` AND `_`/`__` (the underscore forms guarded by a
// word-boundary check so they don't fire inside `snake_case` or URLs), Markdown +
// bare links, and pipe tables. Anything else stays literal text; combined with
// the renderer's `white-space: pre-wrap`, line structure (bullets, paragraphs) is
// preserved as written.

/** An inline run within a paragraph or a table cell. */
export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'link'; href: string; children: InlineNode[] };

export type TableAlign = 'left' | 'center' | 'right' | 'default';

/** A top-level block: either a text run (rendered with pre-wrap) or a table. */
export type ChatBlock =
  | { type: 'text'; nodes: InlineNode[] }
  | { type: 'table'; headers: InlineNode[][]; rows: InlineNode[][][]; aligns: TableAlign[] };

/** Only these schemes become real links — blocks `javascript:` and friends. */
function isSafeHref(href: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

const ALNUM = /[A-Za-z0-9]/;

/**
 * Match an emphasis span (`*…*`, `**…**`, `_…_`, `__…__`) opening at `start`.
 * Returns the inner text and the end index, or null if there is no valid match.
 * CommonMark-flavoured guard: the run just inside the delimiters may not begin or
 * end with whitespace, so "2 * 3 * 4" and "* bullet" stay literal. For the
 * underscore forms (`underscore = true`) the delimiter must also sit on a word
 * boundary at both ends, so `snake_case` / `field_one_two` stay literal.
 */
function matchEmphasis(
  text: string,
  start: number,
  delim: string,
  underscore = false,
): { inner: string; end: number } | null {
  // Underscore opener must not be intra-word: the char before it must be
  // start-of-string or non-alphanumeric.
  if (underscore && start > 0 && ALNUM.test(text[start - 1])) return null;
  const open = start + delim.length;
  if (open >= text.length) return null;
  let from = open;
  for (;;) {
    const close = text.indexOf(delim, from);
    if (close === -1) return null;
    const inner = text.slice(open, close);
    if (inner.length === 0) {
      // Empty run (e.g. the second pair of "****") — keep looking past it.
      from = close + delim.length;
      continue;
    }
    if (/^\s/.test(inner) || /\s$/.test(inner)) return null; // not real emphasis
    // Underscore closer must also be a word boundary, else it's intra-word
    // (e.g. `a_b_c`) — skip this close and keep scanning.
    if (underscore) {
      const after = close + delim.length;
      if (after < text.length && ALNUM.test(text[after])) {
        from = close + delim.length;
        continue;
      }
    }
    return { inner, end: close + delim.length };
  }
}

// The link patterns are sticky (`y`): set to `start`, one matches only there,
// as `^` did on `text.slice(start)`, without copying the rest of the text at
// every `[`, `h` and `w` (most prose has an h or a w every dozen characters, so
// the copies made a long message's parse quadratic). Each match sets its
// `lastIndex` just before it runs, and the parse is synchronous.
/** Label: no nested ] ; href: no whitespace or ) . */
const MD_LINK = /\[([^\]]*)\]\(([^)\s]+)\)/y;
const BARE_URL = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)/iy;

/** Match `[label](href)` opening at `start`. */
function matchMdLink(text: string, start: number): { label: string; href: string; end: number } | null {
  MD_LINK.lastIndex = start;
  const m = MD_LINK.exec(text);
  if (!m) return null;
  const href = m[2];
  if (!isSafeHref(href)) return null;
  return { label: m[1], href, end: start + m[0].length };
}

// Trailing punctuation that is almost never part of a bare URL.
const URL_TRAILING = /[.,!?;:*_~)\]}'"]+$/;

/** Match a bare URL (`https://…` or `www.…`) opening at `start`. */
function matchBareUrl(text: string, start: number): { href: string; display: string; end: number } | null {
  BARE_URL.lastIndex = start;
  const m = BARE_URL.exec(text);
  if (!m) return null;
  let raw = m[1];
  let stripped = 0;
  while (raw.length > 0 && URL_TRAILING.test(raw)) {
    raw = raw.replace(URL_TRAILING, '');
    stripped = m[1].length - raw.length;
    if (stripped >= m[1].length) break;
  }
  if (raw.length === 0) return null;
  const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
  return { href, display: raw, end: start + raw.length };
}

/** Parse one run of inline text into emphasis / link / text nodes. */
function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let buf = '';
  let i = 0;
  const flush = (): void => {
    if (buf) {
      nodes.push({ type: 'text', value: buf });
      buf = '';
    }
  };
  while (i < text.length) {
    const ch = text[i];

    if (ch === '*' || ch === '_') {
      const isDouble = text[i + 1] === ch;
      const delim = isDouble ? ch + ch : ch;
      const em = matchEmphasis(text, i, delim, ch === '_');
      if (em) {
        flush();
        nodes.push({ type: isDouble ? 'strong' : 'em', children: parseInline(em.inner) });
        i = em.end;
        continue;
      }
    }

    if (ch === '[') {
      const link = matchMdLink(text, i);
      if (link) {
        flush();
        nodes.push({ type: 'link', href: link.href, children: parseInline(link.label) });
        i = link.end;
        continue;
      }
    }

    if (ch === 'h' || ch === 'H' || ch === 'w' || ch === 'W') {
      const url = matchBareUrl(text, i);
      if (url) {
        flush();
        nodes.push({ type: 'link', href: url.href, children: [{ type: 'text', value: url.display }] });
        i = url.end;
        continue;
      }
    }

    buf += ch;
    i += 1;
  }
  flush();
  return nodes;
}

/** Visible text of an inline tree — links contribute their label, emphasis its inner text. */
function inlineNodesText(nodes: InlineNode[]): string {
  return nodes.map((n) => (n.type === 'text' ? n.value : inlineNodesText(n.children))).join('');
}

/**
 * Flatten a chat body to its plain visible text — Markdown decoded, not
 * rendered. For single-line surfaces (the inbox row preview) that must never
 * show raw `**`/`[label](url)` markers: emphasis contributes its inner text,
 * links their label, table cells join with spaces, and all whitespace
 * collapses to single spaces (the preview is one ellipsized line).
 */
export function chatBodyPreviewText(body: string): string {
  const parts = parseChatMessageBody(body).map((b) =>
    b.type === 'text'
      ? inlineNodesText(b.nodes)
      : [b.headers, ...b.rows].map((row) => row.map(inlineNodesText).join(' ')).join(' '),
  );
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/** Split a table row into trimmed cell strings, dropping the outer pipes. */
function splitRowCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** A line that could be a table row: contains a pipe and some non-pipe content. */
function isTableRowCandidate(line: string): boolean {
  return line.includes('|') && line.replace(/\|/g, '').trim() !== '';
}

/** The `|---|:--:|` separator row directly under a GFM table header. */
function isDelimiterRow(line: string): boolean {
  if (!line.includes('|') && !/^[\s:|-]+$/.test(line)) return false;
  const cells = splitRowCells(line);
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{1,}:?$/.test(c)) && cells.some((c) => c.length > 0);
}

function parseAligns(delimiterLine: string): TableAlign[] {
  return splitRowCells(delimiterLine).map((c) => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return 'default';
  });
}

/**
 * Parse a chat message body into renderable blocks. Contiguous lines that form a
 * GFM table (a row line immediately followed by a `---` delimiter row) become a
 * `table` block; everything else accumulates into `text` blocks whose inline
 * Markdown is parsed and whose newlines are preserved by the renderer.
 */
export function parseChatMessageBody(body: string): ChatBlock[] {
  const lines = body.split('\n');
  const blocks: ChatBlock[] = [];
  let buf: string[] = [];

  const flushText = (): void => {
    if (buf.length === 0) return;
    const text = buf.join('\n').replace(/^\n+/, '').replace(/\n+$/, '');
    buf = [];
    if (text.trim() !== '') blocks.push({ type: 'text', nodes: parseInline(text) });
  };

  let i = 0;
  while (i < lines.length) {
    const isTableStart =
      i + 1 < lines.length &&
      isTableRowCandidate(lines[i]) &&
      !isDelimiterRow(lines[i]) &&
      isDelimiterRow(lines[i + 1]);

    if (isTableStart) {
      flushText();
      const headerCells = splitRowCells(lines[i]);
      const aligns = parseAligns(lines[i + 1]);
      i += 2;
      const rowLines: string[][] = [];
      while (i < lines.length && isTableRowCandidate(lines[i]) && !isDelimiterRow(lines[i])) {
        rowLines.push(splitRowCells(lines[i]));
        i += 1;
      }
      const colCount = headerCells.length;
      const headers = headerCells.map((c) => parseInline(c));
      const normalizedAligns: TableAlign[] = Array.from({ length: colCount }, (_, k) => aligns[k] ?? 'default');
      const rows = rowLines.map((cells) =>
        Array.from({ length: colCount }, (_, k) => parseInline(cells[k] ?? '')),
      );
      blocks.push({ type: 'table', headers, rows, aligns: normalizedAligns });
      continue;
    }

    buf.push(lines[i]);
    i += 1;
  }
  flushText();
  return blocks;
}
